/** PM Polling — WebSocket-gated AJAX updates, read receipts, member stats.
 *
 * Instead of polling every 6 seconds unconditionally, polls are triggered by
 * WebSocket events (new message, counter update, sync). A 30-second safety-net
 * timer catches anything missed. Falls back to 10-second polling when WebSocket
 * is disconnected.
 */

// Polling mode: 'ws-gated' (default) or 'fallback' (no WebSocket)
var _pollMode = 'ws-gated';
var _safetyNetTimer = null;
var _debouncePollTimer = null;
var _pollInFlight = false;
// Sticky "rebuild the thread list" intent. A reaction does not bump max_msg_id,
// so it asks for a forced rebuild. If a poll is mid-flight the request is
// deferred, and the flag must survive the retry (which carries no argument) so
// the forced rebuild is not silently downgraded to a normal poll.
var _forceThreadsPending = false;
// Monotonic poll counter. Each poll request stamps the value it issued under so
// a response that a newer poll (or a filter switch) has superseded is discarded
// instead of patching a list it no longer matches.
var _pollSeq = 0;
// Thread ids removed locally in the last few seconds. A poll that sampled the
// server just before a delete committed can carry a stale fragment for the
// just-removed thread. This guard makes the delete win: reconcile skips any
// changed row whose id is here.
window._recentlyRemoved = window._recentlyRemoved || {};
const SAFETY_NET_INTERVAL = 30000;
const FALLBACK_INTERVAL = 10000;
const DEBOUNCE_DELAY = 500;

window.append_msgs = function(msg_data, target) {
  let msgs_jq = jQuery(msg_data);

  // Collapse messages from same user
  let last_user = $("#threads_right").find(".msg_username:last").text();
  if (last_user === msgs_jq.find(".msg_username:first").text()) {
    msgs_jq.find(".msg_meta_wrapper:first").remove();
    // Show inline actions that were hidden because header was present
    msgs_jq.filter(".pm_msg:first").find(".msg-inline-actions").removeClass("hidden");
    // Tighten spacing for consecutive same-sender messages
    msgs_jq.filter(".pm_msg:first").removeClass("mt-3").addClass("mt-0.5");
  }

  // With column-reverse, scrollTop=0 is the bottom.
  // Check if user is near bottom BEFORE append (scrollHeight changes after).
  var el = document.getElementById('threads_right');
  var wasAtBottom = !el || Math.abs(el.scrollTop) < 80;

  var wrapper = target.find('#messages_wrapper');
  var appendTarget = wrapper.length ? wrapper : target;
  appendTarget.append(msgs_jq);

  // Initialize Lucide icons in the newly appended content
  msgs_jq.each(function() {
    if (window.LucideIcons && this.nodeType === 1) {
      window.LucideIcons.initialize(this);
    }
  });

  // If conversation empty, remove no_messages indicator
  if (target.find(".no_messages").length) {
    target.find(".no_messages").remove();
  }

  // Consolidate read receipt checkmarks
  if (window.consolidateReadReceipts) {
    window.consolidateReadReceipts();
  }

  // With column-reverse, new content at the end of the wrapper appears at
  // the visual bottom. scrollTop=0 keeps us pinned there.
  if (wasAtBottom && el) {
    el.scrollTop = 0;
  }
};

window.update_pm_info_paged = function(forceThreads) {
  if (forceThreads) {
    _forceThreadsPending = true;
  }
  if (window.pm_ajax_request_active || _pollInFlight) {
    // Run process again in 1 sec (the pending force flag survives the retry).
    window.run_update_process(1000);
    return;
  }
  _pollInFlight = true;

  // If not on first page we dont update threads and messages list
  if (window.get_active_page() != 1) {
    _pollInFlight = false;
    window.run_update_process();
    return;
  }

  let stw = $("#single_thread_wrapper"),
    viewing_thread = stw.length > 0,
    tl = $("#threads_left"),
    thisSeq = ++_pollSeq,
    options = {
      // TS for user stats
      "ts": window.cur_ts
    };

  if (viewing_thread) {
    // Thread id to get new messages
    options.thread = window.get_active_thread_id();
    // Find newest message
    options.newest_msg_id = window.get_newest_msg_id_in_thread();
  } else {
    // max_msg_id to get thread list changes
    options.max_msg_id = tl.data("max-msg-id");
    // Opt in to the row delta (only changed rows); see update_pm_info.
    options.delta = 1;
    // Rebuild the thread list even when max_msg_id is unchanged (e.g. a
    // reaction changed an inbox snippet without a new message).
    options.force_threads = _forceThreadsPending ? 1 : 0;
  }

  $.ajax({
    type: "POST",
    url: "/app/messages/update",
    data: options,
    dataType: "json"
  }).done(function(data, textStatus, jqXHR) {
    // Auth/CSRF/onboarding gate returns {redirect: url} for a lapsed session or
    // stale token. The global ajaxComplete handler navigates, so just stop here.
    if (data && typeof data.redirect === 'string' && data.redirect) {
      return;
    }
    if (data?.not_logged_in == 1) {
      window.location.href = window.location.protocol + "//" +
        window.location.hostname + window.location.pathname;
      return;
    }

    // Update member stats if any
    if (typeof data.member_stats != "undefined") {
      window.update_member_stats_messages(data.member_stats);
    }

    let c = $("#conversation");

    if (viewing_thread) {
      // If new messages arrived
      if (typeof data.new_messages != "undefined" && data.new_messages.length > 0) {
        window.messages_highlighted = 1;
        // Remove "new" in 3 seconds
        window.setTimeout(function() {
          window.remove_new_mark();
        }, 3000);

        c.prepend(data.new_messages);
        // If more than 25 msgs - remove
        c.find(".pm_msg:gt(24)").remove();
      }

      // Update "last opened by contact" timestamp
      if (typeof data.lobc != "undefined") {
        /*
         Remove "unread" mark
         Check if timestamp changed
         Microseconds needed
         */
        let l = c.data("lobc");
        // Update only if TS changed
        if (data.lobc > 0 && l < data.lobc) {
          c.data("lobc", data.lobc);
          // Remove unread status and update read receipts
          c.find(".pm_unread").removeClass("pm_unread");
          window.updateReadReceipts();
        }
      }
    } else {
      // Discard if a newer poll superseded this one (paged sidebar has no
      // filter switch, so the seq check is the only staleness guard here).
      var listStale = thisSeq !== _pollSeq;
      // Consume the forced-rebuild intent only when we apply the response, so a
      // discarded stale poll keeps the flag set for the next poll.
      if (!listStale) {
        _forceThreadsPending = false;
      }
      if (!listStale && data.threads_delta) {
        // Delta path: patch only changed rows in place.
        window.reconcileThreadRows(data.threads_delta);
      } else if (!listStale && data.threads_update && data.max_msg_id) {
        // Full-rebuild fallback.
        tl.data("max-msg-id", data.max_msg_id);
        tl.empty();
        tl.append(data.threads_update);
        if (window.LucideIcons) {
          window.LucideIcons.initialize(tl[0]);
        }
        if (window.initializeThreadActions) {
          window.initializeThreadActions();
        }
      }
    }
  }).always(function() {
    _pollInFlight = false;
    window.run_update_process();
  });
};

window.update_pm_info = function(forceThreads) {
  if (forceThreads) {
    _forceThreadsPending = true;
  }
  if (window.pm_ajax_request_active || _pollInFlight) {
    // Run process again in 1 sec (the pending force flag survives the retry).
    window.run_update_process(1000);
    return;
  }
  _pollInFlight = true;

  let threadsLeft = $("#threads_left"),
    filtersBlock = $("#filters"),
    filter = filtersBlock.data("filter"),
    newestMsgIdInThread = window.get_newest_msg_id_in_thread(),
    thisSeq = ++_pollSeq;

  $.ajax({
    type: "POST",
    url: "/app/messages/update",
    data: {
      // Send this to get new messages
      "thread": window.get_active_thread_id(),
      // Active filter
      "filter": filter,
      // Find newest message
      "newest_msg_id": newestMsgIdInThread,
      // Send this to get thread list changes
      "max_msg_id": threadsLeft.data("max-msg-id"),
      "threads_count": threadsLeft.find(".pm_thread").length,
      // Opt in to the row delta (only changed rows). When a delta can't be
      // produced the server returns a full threads_update instead, which the
      // client detects by the absence of threads_delta.
      "delta": 1,
      // Rebuild the thread list even when max_msg_id is unchanged (e.g. a
      // reaction changed an inbox snippet without a new message).
      "force_threads": _forceThreadsPending ? 1 : 0,
      // TS for user stats
      "ts": window.cur_ts
    },
    dataType: "json"
  }).done(function(data, textStatus, jqXHR) {
    // Auth/CSRF/onboarding gate returns {redirect: url} for a lapsed session or
    // stale token. The global ajaxComplete handler navigates, so just stop here.
    if (data && typeof data.redirect === 'string' && data.redirect) {
      return;
    }
    // cast as int
    if (data.not_logged_in == 1) {
      window.location.href = window.location.protocol + "//" +
        window.location.hostname + window.location.pathname;
      return;
    }

    // Update member stats if any
    if (typeof data.member_stats != "undefined") {
      window.update_member_stats_messages(data.member_stats);
    }

    // Discard a thread-list update the user has moved past: a newer poll has
    // been issued, or the filter changed since this request went out. Patches
    // and the cursor are both dropped so the next same-filter poll re-fetches
    // and re-applies (one redundant fetch, never a stale or skipped row).
    var listStale = (thisSeq !== _pollSeq) || (filtersBlock.data("filter") !== filter);

    // The forced rebuild (if any) was carried by this request, but only consume
    // the pending flag when we actually apply the response. A discarded stale
    // poll keeps the flag set so the next same-filter poll re-forces the rebuild.
    if (!listStale) {
      _forceThreadsPending = false;
    }

    if (!listStale && data.threads_delta) {
      // Delta path: patch only the changed rows in place, no list teardown.
      window.reconcileThreadRows(data.threads_delta);
    } else if (!listStale && data.threads_update && data.max_msg_id) {
      // Full-rebuild fallback (cold cursor, too many changes, kill-switch off,
      // reaction-only force, or a non-'all' filter).
      // Update 'max_msg_id'
      threadsLeft.data("max-msg-id", data.max_msg_id);

      let activeThreadId = window.get_active_thread_id();
      let savedScrollTop = threadsLeft[0].scrollTop;
      // Parse new content into a fragment, then swap in one operation to avoid blink
      var tmp = document.createElement('div');
      tmp.innerHTML = data.threads_update;
      var frag = document.createDocumentFragment();
      while (tmp.firstChild) frag.appendChild(tmp.firstChild);
      while (threadsLeft[0].firstChild) {
        threadsLeft[0].removeChild(threadsLeft[0].firstChild);
      }
      threadsLeft[0].appendChild(frag);
      threadsLeft[0].scrollTop = savedScrollTop;
      requestAnimationFrame(function() { threadsLeft[0].scrollTop = savedScrollTop; });

      // Initialize Lucide icons in the replaced thread list
      if (window.LucideIcons) {
        window.LucideIcons.initialize(threadsLeft[0]);
      }

      // Re-initialize thread action buttons (desktop dropdown vs mobile swipe)
      if (window.initializeThreadActions) {
        window.initializeThreadActions();
      }

      // Reset infinite scroll state after thread list replacement
      window._threadsFullyLoaded = !document.getElementById('load_more_conversations');

      if (activeThreadId && $("#thread_" + activeThreadId).length) {
        // If threads was reloaded and there was active thread -
        // set active thread again, without reloading messages
        window.view_thread(activeThreadId, false);
        threadsLeft[0].scrollTop = savedScrollTop;
        setTimeout(function() { threadsLeft[0].scrollTop = savedScrollTop; }, 0);
        setTimeout(function() { threadsLeft[0].scrollTop = savedScrollTop; }, 50);
      }
    }

    let activeThreadId1 = window.get_active_thread_id();
    // Update only if thread_id same
    if (typeof data.thread_id == "undefined" || data.thread_id !== activeThreadId1) return;

    // Update "last opened by contact" timestamp
    if (typeof data.lobc != "undefined") {
      window.update_unread(activeThreadId1, data.lobc, data.new_messages.length);
    }

    // Update header status from server response
    if (typeof data.contact_status != "undefined") {
      window.update_header_status(data.contact_status);
    }

    // Hide compose box and update header when the contact is inactive/deleted
    if (data.contact_active === false) {
      $("#threads_right_bottom").addClass("hidden");
      if (data.contact_name) {
        $("#current_thread_username").text(data.contact_name);
      }
    }

    // If new messages arrived
    if (data.new_messages) {
      if (!document.hasFocus() && typeof window.flashTitle === 'function') {
        window.flashTitle('**** ' + window.t('legacy.pm.new_message_alert') + ' ****');
      }

      // In paginated view, skip appending new messages to avoid breaking pagination
      if (window.pagedViewActive) {
        window.run_update_process();
        return;
      }

      window.messages_highlighted = 1;
      // Remove "new" in 3 seconds
      window.setTimeout(function() {
        window.remove_new_mark();
      }, 3000);

      // Find last date divider
      let threadsRight = $("#threads_right"),
        first_datediv = threadsRight.find(".pm_date_div:last");

      // Append new messages to message list
      window.append_msgs(data.new_messages, threadsRight);

      // Remove dup date dividers
      var date = first_datediv.data("date"),
        last_datediv = $("#threads_right").find("div[data-date=" + date + "]:last");
      if (last_datediv.length > 0) {
        last_datediv.remove();
      }

    }
  }).always(function() {
    _pollInFlight = false;
    window.run_update_process();
  });
};

window.remove_new_mark = function() {
  if ($("#single_thread_wrapper").length > 0) {
    // Classic view
    $("#conversation").find(".pm_new").removeClass("pm_new");
  } else {
    // Split view
    $("#threads_right").find(".pm_new").removeClass("pm_new");
  }
};

window.update_unread = function(thread_id, lobc, force_check) {
  let thread = $("#thread_" + thread_id);
  force_check = typeof force_check != "undefined";
  // Check if timestamp changed
  // Microseconds needed
  let l = thread.data("lobc");
  // Update only if TS changed OR force_check = true
  if (force_check || (lobc > 0 && l < lobc)) {
    thread.data("lobc", lobc);
    // Remove unread status and update read receipts
    var $unread = $("#threads_right").find(".pm_unread");
    if ($unread.length) {
      $unread.removeClass("pm_unread");
      window.updateReadReceipts();
    }
  }
};

window.updateReadReceipts = function() {
  // Update unread→read: swap icon and status on wrappers
  document.querySelectorAll("#threads_right .own_msg:not(.pm_unread) .read-receipt-wrap[data-status='unread']").forEach(function(wrap) {
    wrap.setAttribute("data-status", "read");
    var icon = wrap.querySelector("[data-lucide]");
    if (icon) {
      icon.setAttribute("data-lucide", "check-check");
      icon.classList.remove("text-gray-400");
      icon.classList.add("text-sky-400");
    }
    // Re-render the Lucide SVG
    if (window.LucideIcons) {
      window.LucideIcons.initialize(wrap);
    }
  });
  // Re-consolidate after status changes
  window.consolidateReadReceipts();
};

window.consolidateReadReceipts = function() {
  var msgs = document.querySelectorAll("#messages_wrapper > .pm_msg");
  var run = [];
  var lastStatus = null;

  function flushRun() {
    if (run.length === 0) return;
    for (var i = 0; i < run.length - 1; i++) {
      run[i].classList.add("hidden");
      run[i].classList.remove("flex");
    }
    run[run.length - 1].classList.remove("hidden");
    run[run.length - 1].classList.add("flex");
    run = [];
  }

  msgs.forEach(function(msg) {
    var wrap = msg.querySelector(".read-receipt-wrap");
    if (!wrap) {
      flushRun();
      lastStatus = null;
      return;
    }

    var status = wrap.getAttribute("data-status");

    if (lastStatus !== null && lastStatus !== status) {
      flushRun();
    }

    run.push(wrap);
    lastStatus = status;
  });

  flushRun();
};

/**
 * Schedule the next poll. In ws-gated mode, this sets the safety-net timer
 * (30s). In fallback mode (no WebSocket), it polls every 10s.
 * Explicit interval overrides (e.g. retry-in-1s) are honored regardless of mode.
 */
window.run_update_process = function(interval) {
  clearTimeout(_safetyNetTimer);

  if (typeof interval != "undefined") {
    // Explicit interval requested (e.g. retry after busy)
    const updateFn = window.pm_view_mode === "paged" ? window.update_pm_info_paged : window.update_pm_info;
    _safetyNetTimer = window.setTimeout(updateFn, interval);
    return;
  }

  var delay = _pollMode === 'ws-gated' ? SAFETY_NET_INTERVAL : FALLBACK_INTERVAL;
  const updateFn = window.pm_view_mode === "paged" ? window.update_pm_info_paged : window.update_pm_info;
  _safetyNetTimer = window.setTimeout(updateFn, delay);
};

/**
 * Request an immediate poll, debounced to batch rapid WebSocket events.
 * Skipped if a poll is already in flight.
 */
window.requestPoll = function() {
  if (_pollInFlight) return;
  clearTimeout(_debouncePollTimer);
  _debouncePollTimer = setTimeout(function() {
    clearTimeout(_safetyNetTimer);
    const updateFn = window.pm_view_mode === "paged" ? window.update_pm_info_paged : window.update_pm_info;
    updateFn();
  }, DEBOUNCE_DELAY);
};

// ─── Inbox row reconcile ─────────────────────────────────────────────
//
// Patch only the thread rows that changed since the client's cursor, in place,
// instead of tearing down and rebuilding #threads_left. Keeps scroll position,
// keeps rows scrolled past the server's render cap, and skips re-rendering the
// whole list on every message. The full-rebuild path above is the fallback.

// Compare two message ids as bigints (they exceed Number.MAX_SAFE_INTEGER, so
// never parseInt them). Decimal strings: shorter is smaller, equal length sorts
// lexicographically. Returns -1 / 0 / 1.
function compareMsgId(a, b) {
  var sa = String(a == null ? '' : a);
  var sb = String(b == null ? '' : b);
  if (sa.length !== sb.length) return sa.length < sb.length ? -1 : 1;
  if (sa === sb) return 0;
  return sa < sb ? -1 : 1;
}
// Exposed so the node unit test reaches it without depending on vm hoisting.
window.compareMsgId = compareMsgId;

function threadRows(container) {
  return Array.prototype.filter.call(container.children, function(el) {
    return el.classList && el.classList.contains('pm_thread');
  });
}

function rowLastMsgId(node) {
  return (node && node.dataset && node.dataset.lastMsgId) || '0';
}

function parseRowFragment(html) {
  var tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.firstElementChild;
}

function reinitRow(node) {
  if (window.LucideIcons && node) {
    window.LucideIcons.initialize(node);
  }
}

// Insert node so the list stays sorted by last_msg_id DESC: before the first
// row whose id is lower. A new-message row (the new global max) lands at the top.
function insertSorted(container, node, lastMsgId) {
  var rows = threadRows(container);
  for (var i = 0; i < rows.length; i++) {
    if (compareMsgId(rowLastMsgId(rows[i]), lastMsgId) < 0) {
      container.insertBefore(node, rows[i]);
      return;
    }
  }
  // Lower than every loaded row: drop it just above the load-more sentinel so
  // pagination order stays intact, else at the end.
  var sentinel = document.getElementById('load_more_conversations');
  if (sentinel && sentinel.parentNode === container) {
    container.insertBefore(node, sentinel);
  } else {
    container.appendChild(node);
  }
}

// Move each changed row (except the read-only active one, whose position is
// unchanged) to its sorted slot. Only changed nodes move, so unchanged rows
// keep their relative order — the scroll anchor depends on that.
function reorderChanged(container, changed, active, insertedIds) {
  for (var i = 0; i < changed.length; i++) {
    var id = String(changed[i].thread_id);
    if (id === active) continue;
    if (window._recentlyRemoved[id]) continue;
    // A row just inserted in the patch pass is already at its sorted slot.
    if (insertedIds && insertedIds[id]) continue;
    var node = document.getElementById('thread_' + id);
    if (!node || node.parentNode !== container) continue;
    container.removeChild(node);
    insertSorted(container, node, changed[i].last_msg_id);
  }
}

// Keep .active on (and only on) the currently-open thread. Uses the live active
// id, not a stale one from the response, so a mid-poll thread switch is safe.
function reapplyActive(activeId) {
  var container = document.getElementById('threads_left');
  if (!container) return;
  var active = container.querySelectorAll('.pm_thread.active');
  for (var i = 0; i < active.length; i++) active[i].classList.remove('active');
  if (activeId) {
    var row = document.getElementById('thread_' + activeId);
    if (row) row.classList.add('active');
  }
}

// Record the topmost visible UNCHANGED rows (up to 3) and their offset relative
// to the viewport top, BEFORE any mutation. An unchanged row's own height is
// stable, so restoring it to the same viewport offset keeps the view steady
// while rows above absorb the height change.
function captureScrollAnchor(container, changedIds) {
  if (container.scrollTop <= 2) return { atTop: true };
  var rows = threadRows(container);
  var candidates = [], delta = {}, i, row;
  var viewportBottom = container.scrollTop + container.clientHeight;
  for (i = 0; i < rows.length; i++) {
    row = rows[i];
    // Only anchor on a row that actually intersects the visible viewport, so the
    // restored position keeps an on-screen row stationary (an off-screen anchor
    // could let the visible region shift).
    if (row.offsetTop + row.offsetHeight > container.scrollTop && row.offsetTop < viewportBottom) {
      if (!changedIds[row.dataset.threadId]) {
        candidates.push(row.dataset.threadId);
        delta[row.dataset.threadId] = row.offsetTop - container.scrollTop;
        if (candidates.length >= 3) break;
      }
    }
  }
  // When every visible row is changing (rare), there is no stable anchor: a
  // changed row's height can shift on re-render, so anchoring on one would make
  // the restore offset wrong. Leave candidates empty and let restoreScrollAnchor
  // fall through to clamping the current scrollTop into range.
  return { atTop: false, candidates: candidates, delta: delta };
}

// Restore scroll AFTER mutation, clamped, falling through candidates in case the
// chosen anchor row was removed in the meantime (e.g. by a concurrent delete).
function restoreScrollAnchor(container, anchor) {
  var maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
  if (anchor.atTop) { container.scrollTop = 0; return; }
  for (var i = 0; i < anchor.candidates.length; i++) {
    var id = anchor.candidates[i];
    var row = document.getElementById('thread_' + id);
    if (row && row.parentNode === container) {
      container.scrollTop = Math.max(0, Math.min(Math.round(row.offsetTop - anchor.delta[id]), maxTop));
      return;
    }
  }
  // No anchor survived: at least keep scrollTop in range (never past the end).
  container.scrollTop = Math.max(0, Math.min(container.scrollTop, maxTop));
}

/**
 * Apply a server row delta to #threads_left in place. delta = { changed:
 * [{thread_id, last_msg_id, html}], active_thread_id, max_msg_id }.
 */
window.reconcileThreadRows = function(delta) {
  var container = document.getElementById('threads_left');
  if (!container) return;
  var changed = delta.changed || [];
  var active = delta.active_thread_id != null ? String(delta.active_thread_id) : null;

  var changedIds = {};
  for (var i = 0; i < changed.length; i++) changedIds[String(changed[i].thread_id)] = true;

  var anchor = captureScrollAnchor(container, changedIds);

  // 1. Patch existing rows / insert new ones. One node per thread id.
  var insertedIds = {};
  for (var k = 0; k < changed.length; k++) {
    var c = changed[k];
    var id = String(c.thread_id);
    // A local delete wins over a stale in-flight delta re-adding the row.
    if (window._recentlyRemoved[id]) continue;
    var node = parseRowFragment(c.html);
    if (!node) continue;
    var existing = document.getElementById('thread_' + id);
    if (existing) {
      existing.replaceWith(node);
      reinitRow(node);
    } else if (id === active) {
      // Read-only active change with no loaded row (active thread below the
      // floor): nothing on-screen to update, so skip rather than inject a
      // duplicate that would also live deep in the list.
      continue;
    } else {
      insertSorted(container, node, c.last_msg_id);
      reinitRow(node);
      insertedIds[id] = true;
    }
  }

  // 2. Move changed rows to their sorted slots. Newly inserted rows are already
  //    at the right position, and the read-only active row stays put.
  reorderChanged(container, changed, active, insertedIds);

  // 3. Rebind action buttons once (handlers are delegated; this toggles the
  //    desktop-dropdown vs mobile-swipe affordance on the touched rows).
  if (window.initializeThreadActions) window.initializeThreadActions();

  // 4. Advance the cursor (jQuery .data, matching the rest of polling).
  if (delta.max_msg_id) $(container).data('max-msg-id', delta.max_msg_id);

  // 5. Keep the active highlight on the open thread; never reload its messages.
  reapplyActive(window.get_active_thread_id ? window.get_active_thread_id() : null);

  // 6. Restore scroll with a single clamped write.
  restoreScrollAnchor(container, anchor);
};

/**
 * Remove a thread row in place (cross-device delete / blocked-by-other-user),
 * anchoring scroll so a removal above the viewport doesn't shift visible rows.
 */
window.removeThreadRow = function(threadId) {
  var id = String(threadId);
  // Guard a brief window so a stale in-flight delta can't re-add the row.
  window._recentlyRemoved[id] = true;
  setTimeout(function() { delete window._recentlyRemoved[id]; }, 5000);

  var container = document.getElementById('threads_left');
  var row = container ? document.getElementById('thread_' + id) : null;
  if (!container || !row) return;

  var anchor = captureScrollAnchor(container, {});
  row.remove();

  // Close the open pane if it was showing the removed thread (mirrors the
  // local delete close in crud.js).
  if (window.get_active_thread_id && String(window.get_active_thread_id()) === id) {
    if (window.pm_view_mode === 'chat') {
      $('#threads_main').removeClass('threads--open');
      if (typeof $.scrollTo === 'function' && $('.cCont').length) {
        $.scrollTo($('.cCont'));
      }
      $('#threads_right').empty();
    }
  }

  restoreScrollAnchor(container, anchor);
};

// ─── Message recall (unsend) removal ──────────────────────────
//
// The append-only message poll only carries *new* messages, so a recall of an
// already-rendered bubble needs a dedicated path. The realtime
// THREAD_MESSAGE_RECALLED event (websocket-client.js) dispatches
// 'thread:recalled'; this removes the matching #msg_{id} bubble. The sender's
// own removal is driven by the API response, so this handler is idempotent
// (no-op if the bubble is already gone).

/**
 * Remove a recalled (unsent) message bubble from the open conversation.
 * Idempotent: a bubble already removed is a no-op. message_id / thread_id are
 * strings (they exceed Number.MAX_SAFE_INTEGER).
 *
 * Sibling: applyRecallRemoval() in pm/crud.js drives the same removal for the
 * sender's own API-response path.
 */
window.applyMessageRecallRemoval = function(message_id, thread_id) {
  var node = document.getElementById('msg_' + String(message_id));
  if (node) {
    node.remove();
  }

  // Refresh the thread-list snippet: recall rolls it back to the prior message
  // server-side, and the delta poll picks that up.
  if (window.requestPoll) {
    window.requestPoll();
  }
};

// WebSocket event listeners — trigger polls on relevant events.
// Gated to the PM page: this module ships in the global interpals bundle,
// but /app/messages/update has nothing to refresh elsewhere. Header badges
// on non-PM pages are kept in sync directly by websocket-client.js off the
// COUNTER_UPDATE payload.
if (document.querySelector('.privateMessages')) {
  document.addEventListener('thread:newmessage', function() {
    window.requestPoll();
  });

  document.addEventListener('ws:counter-update', function() {
    window.requestPoll();
  });

  document.addEventListener('thread:deleted', function(e) {
    if (e.detail && e.detail.thread_id) {
      window.removeThreadRow(e.detail.thread_id);
    }
  });

  document.addEventListener('thread:recalled', function(e) {
    if (e.detail && e.detail.message_id) {
      window.applyMessageRecallRemoval(e.detail.message_id, e.detail.thread_id);
    }
  });

  var _wsHasConnectedBefore = false;
  document.addEventListener('ws:connected', function() {
    _pollMode = 'ws-gated';
    if (_wsHasConnectedBefore) {
      // Sync state after reconnect. Skip on first connect, the initial poll covers it.
      window.requestPoll();
    }
    _wsHasConnectedBefore = true;
  });

  document.addEventListener('ws:disconnected', function() {
    _pollMode = 'fallback';
    // Switch to fallback polling immediately
    window.run_update_process();
  });
}

window.update_member_stats_messages = function(member_stats) {
  // Visual count display for the /messages page header badges. Title-flash
  // for counter changes lives in websocket-client.js (driven by the
  // ws:counter-update event), so this function only writes the badge text.
  function setCountRaw(stats, rawId, displayId) {
    var rawValue = stats[rawId] || '';
    var newValue = parseInt(rawValue) || 0;

    // Display "99+" for counts greater than 99, otherwise show the actual number
    var displayValue = rawValue;
    if (newValue > 99) {
      displayValue = '99+';
    }

    $('#' + displayId).text(displayValue ? '(+' + displayValue + ')' : '');
  }

  setCountRaw(member_stats, 'pmNewCnt_raw', 'pmNewCnt');
  setCountRaw(member_stats, 'wpNewCnt_raw', 'wpNewCnt');
  setCountRaw(member_stats, 'frNewCnt_raw', 'frNewCnt');
  setCountRaw(member_stats, 'phNewCnt_raw', 'phNewCnt');
  setCountRaw(member_stats, 'ncNewCnt_raw', 'ncNewCnt');

  if (window.enable_notifs) {
    window.showNotifications(member_stats.msgs);
  }
  window.cur_ts = member_stats.ts;
};
